// ============================================================================
// TEXT-NATIVE REPORT PDF — real text, drawn with the jsPDF text API.
// ============================================================================
// WHY THIS FILE EXISTS
// The shipping export photographs the DOM (html2canvas) and embeds the JPEG. The result has ZERO
// extractable characters: nothing selectable, nothing searchable, nothing for a screen reader, and
// nothing any text checker can read. A leak sweep reporting "clean" on a page it cannot read is a
// false pass, so the PDF was the one customer surface no standard could reach.
//
// WHY IT IS ITS OWN MODULE, NOT INLINE IN App.js
// The layout has exactly ONE home. App.js draws the customer's PDF from here, and the headless
// verifier draws the PDF IT CHECKS from here too. If these were two copies, a green verification
// would prove nothing about the artifact a family receives.
//
// FIDELITY: every value printed here is READ FROM THE ROW, never recomputed. Where a value is
// absent the section is omitted and named in "NOT EVALUATED BY BBOT" — never estimated, per the
// absolute rule.
//
// ENCODING: helvetica is WinAnsi. "·", "–" and "°" are in that set and print correctly; an arrow
// (→) is NOT, and would silently become garbage. Nothing here emits one.

// ONE definition of "is this tool measured?", shared with App.js. Holds no React and imports
// nothing, so the headless verifier that draws its PDF from this module is unaffected.
import { toolIsMeasured } from "./bbotMeasured";

var PT = {
  W: 612, H: 792, M: 40,
  BLUE: [41, 112, 184], CORAL: [232, 82, 106], NAVY: [15, 23, 42],
  G700: [51, 65, 85], G600: [71, 85, 105], G500: [100, 116, 139],
  G200: [226, 232, 240], G100: [241, 245, 249], G50: [248, 250, 252],
  WHITE: [255, 255, 255], AMBER_BG: [254, 243, 205], AMBER_FG: [139, 105, 20],
  OK_BG: [209, 250, 229], OK_FG: [6, 95, 70], ORANGE: [249, 115, 22]
};

function cleanToolLabel(lbl) {
  return String(lbl || "").replace(/\s*\(.*?\)\s*/g, "").trim();
}

// THE ABSENCE PHRASES — one definition, used both to route a section into "NOT EVALUATED" and to
// keep it out of the graded list. Two copies of this regex would eventually disagree and a tool
// would appear in both places at once.
var ABSENCE = /not observ|not applicable|not measured|not evaluable|requires .*footage|n\/a|pending/;

var PITCHER_HIDE = ["run", "receiving", "fielding", "plateDis", "batBall", "exitVelo", "popTime", "throw"];
var PITCHER_TOOLS = ["fastball", "fastballvelo", "pitchvelo", "velocity", "armaction", "mechanics",
  "movement", "pitchmovement", "spinning", "spin", "changingspeeds", "command", "mix", "delivery", "athleticism"];

// ---------------------------------------------------------------------------
// THE MODEL — the same derivation the on-screen PDF component performs, in one place, so the
// text-native page and the rasterised page cannot describe different sessions.
// ---------------------------------------------------------------------------
export function deriveReportModel(r, sc) {
  if (!r) return null;
  var report = r.fullReport || r;
  var sessionMode = String(r.sessionMode || report.sessionMode || "").toLowerCase();
  var modeLabel = sessionMode === "pickup" ? " · Pickup Game" :
                  sessionMode === "multi" ? " · Group Showcase" :
                  sessionMode === "individual" ? " · Individual" : "";

  var posRaw = String((sc && sc.position) || r.position || "").trim().split(/[\s,/]/)[0];
  var isPitcher = (r.isPitcher === true) || /^(P|RHP|LHP|SP|RP|CP)$/i.test(posRaw);

  function isPitcherTool(s) {
    var k = String(s.key || "").toLowerCase().replace(/[^a-z]/g, "");
    var lbl = String(s.label || "").toLowerCase();
    if (PITCHER_TOOLS.indexOf(k) >= 0) return true;
    return /arm action|mechanic|pitch movement|spinning|changing speed|fastball|pitch velocity|delivery|command|pitch mix/.test(lbl);
  }
  function keepSection(s) {
    return isPitcher ? PITCHER_HIDE.indexOf(s.key) < 0 : !isPitcherTool(s);
  }

  var allSections = (r.sections || (report && report.sections) || []).filter(keepSection);
  var mv = r.measuredValues || report.measuredValues || {};
  // Read the same two ways `mv` is read above, so an admin-verified time counts here whichever
  // shape the caller handed in. `toolIsMeasured` uses it to credit a hand-timed mark whose section
  // never got a percentile.
  var mvb = r.measuredValuesBasis || (report && report.measuredValuesBasis) || {};

  function chip(label) {
    var c = (r.summaryChips || []).find(function (x) {
      return String(x.label || "").toLowerCase().indexOf(label.toLowerCase()) >= 0;
    });
    return c ? c.value : null;
  }

  var stats = [
    ["H-TO-1ST", chip("h-to-1") || chip("home") || (mv.h2f ? mv.h2f + "s" : null)],
    ["TOP EV", chip("ev") || chip("exit") || (mv.exitVeloPeak ? mv.exitVeloPeak + " mph" : null)],
    ["THROW", chip("throw") || chip("arm") || (mv.throwVelo ? mv.throwVelo + " mph" : null)]
  ].filter(function (s) { return s[1]; });

  function isAbsent(s) {
    var txt = String(s.text || "").trim();
    if (!txt) return true;
    return ABSENCE.test(String(s.source || "").toLowerCase()) || ABSENCE.test(txt.toLowerCase().slice(0, 60));
  }

  // MECHANICS: reported only where the SERIES exists. A single reading has no rep-to-rep spread to
  // state, so the row is dropped rather than printed as a range of one.
  // THE CREDIBILITY LAW ON THE PRINT PATH (2026-08-13). This read the raw per-rep list behind a
  // TWO-rep floor -- weaker even than the screen report's five -- and printed a min-max range from
  // it. A printed number outlives every surface it was copied from, so the print path is the last
  // place a value should be easier to publish than on screen. It now renders from the SAME signed
  // block as everything else: the six receipts and the 5-rep floor, or nothing.
  var pm = r.poseMetrics || report.poseMetrics || null;
  var mech = null;
  var _blk = r.mechanicsCertified || report.mechanicsCertified ||
             ((report.full_report || {}) || {}).mechanicsCertified || null;
  var _NEED = ["pool_clean", "anatomy_verified", "scale_hand_measured", "reproduced_from_record",
               "scale_domain_stable", "plant_event_present"];
  var _signed = {};
  if (_blk && _blk.metrics && Array.isArray(_blk.reps)) {
    Object.keys(_blk.metrics).forEach(function (k) {
      var rc = (_blk.metrics[k] || {}).receipts || {};
      if (_NEED.every(function (x) { return rc[x] === true; })) _signed[k] = true;
    });
  }
  if (pm && Object.keys(_signed).length) {
    // `swings` was carried over from the old two-source shape (deliveries OR swings, whichever
    // had >=2 entries). The signed block has ONE rep list, so the second variable had nothing to
    // hold -- and under CI, where warnings are errors, an unused variable is a failed deploy.
    var deliveries = (_blk.reps || []);
    if (deliveries.length >= 5) {
      var repData = deliveries;
      var repType = String(pm.kind || "").indexOf("pitch") === 0 ? "delivery" : "swing";
      // ONLY THE SIGNED SET, AND ONLY THE KEYS THIS ROW SIGNED. The pitcher/hitter metric lists are
      // gone: a list of what BBOT would like to print is not a list of what it may print.
      var LABELS = {
        stride_length_direct_pct: { label: "Stride", unit: "%" },
        weight_shift_pct: { label: "Weight shift", unit: "%" },
        head_movement_pct: { label: "Head move", unit: "%" }
      };
      var metrics = Object.keys(_signed).filter(function (k) { return LABELS[k]; })
        .map(function (k) { return { key: k, label: LABELS[k].label, unit: LABELS[k].unit }; });
      var rows = metrics.map(function (m) {
        var vals = repData.map(function (d) { return d[m.key]; })
          .filter(function (v) { return v != null; });
        if (vals.length < 5) return null;
        return { m: m, vals: vals, mn: Math.min.apply(null, vals), mx: Math.max.apply(null, vals) };
      }).filter(Boolean);
      if (rows.length) mech = { rows: rows, repType: repType, repCount: repData.length };
    }
  }

  return {
    identity: sc ? {
      name: ((sc.firstName || "") + " " + (sc.lastName || "")).trim(),
      meta: [sc.position, sc.gradYear ? ("Class " + sc.gradYear) : null, sc.height,
        sc.weight ? (sc.weight + " lbs") : null,
        (sc.bats || sc.throws) ? ((sc.bats || "-") + "/" + (sc.throws || "-")) : null
      ].filter(Boolean).join(" · "),
      bats: sc.bats || "?", throws: sc.throws || "?"
    } : null,
    sessionType: (r.sessionType || "Evaluation") + modeLabel,
    date: r.date || r.sessionDate || "",
    evaluator: r.evaluator || "BBOT",
    isPartial: String(r.evaluationScope || report.evaluationScope || "").toLowerCase() === "partial",
    partialText: r.partialWarning || report.partialWarning || "",
    stats: stats,
    summary: r.summary || "",
    sessionContext: r.sessionContext || report.sessionContext || "",
    // THE SPLIT IS ON THE VALUE, NOT THE GRADE (2026-10-09).
    //
    // All three filters used to key on `s.grade`, and the zero-grades law removes the grade from
    // precisely the tools that ARE measured. So the day it was enforced everywhere, TOOL GRADES
    // emptied on the downloadable PDF and every device-measured tool slid into OBSERVATIONS: a
    // hand-timed pop time, printed as somebody's impression, in the one artifact a family keeps.
    //
    // `toolIsMeasured` is imported rather than written again here. This module is standalone on
    // purpose -- the customer's PDF and the verifier's PDF are the same code -- and that argument
    // is for the LAYOUT having one home, not for this test having two. The helper holds no React
    // and imports nothing, so the verifier is unaffected. [[admission-is-the-value]]
    graded: allSections.filter(function (s) { return toolIsMeasured(s, mvb); }),
    observations: allSections.filter(function (s) {
      return !toolIsMeasured(s, mvb) && s.text && !isAbsent(s);
    }),
    mechanics: mech,
    // A TOOL CANNOT BE BOTH MEASURED AND NOT EVALUATED. The absence test reads `source`, and a
    // measured tool whose source is still marked pending was landing in TOOL GRADES as "Hit 60" and
    // in NOT EVALUATED as "Hit" on the same page. A MEASUREMENT is an evaluation, so carrying one
    // settles it; what the absence test then describes is a missing WRITE-UP, not a missing
    // evaluation. (Keyed on the measurement now, for the same reason as the two filters above --
    // keyed on the grade, every measured tool would be listed as not evaluated.)
    notEvaluated: allSections.filter(function (s) { return !toolIsMeasured(s, mvb) && isAbsent(s); })
      .map(function (s) { return cleanToolLabel(s.label); })
  };
}

// ---------------------------------------------------------------------------
// THE PAGE
// ---------------------------------------------------------------------------
export function buildReportPdf(jsPDFCtor, r, sc) {
  var M = deriveReportModel(r, sc);
  if (!M) return null;

  var doc = new jsPDFCtor({ unit: "pt", format: "letter", orientation: "portrait" });
  var x0 = PT.M, W = PT.W - PT.M * 2, y = PT.M;

  function rgb(fn, c) { doc[fn](c[0], c[1], c[2]); }
  function font(size, style, color) {
    doc.setFont("helvetica", style || "normal");
    doc.setFontSize(size);
    rgb("setTextColor", color || PT.NAVY);
  }
  // PAGE BREAK BEFORE THE BLOCK, NOT MID-BLOCK: a tool grade split across a page boundary from its
  // own text is how a value ends up reading against the wrong label.
  // ORPHAN CONTROL. A short trailing block that misses the bottom margin by a few points was
  // starting a second page to carry ONE LINE, which reads as a broken document rather than a long
  // one. A block may borrow up to SLACK points of the bottom margin instead of orphaning; 20pt
  // leaves 20pt of margin, still clear of any printer's unprintable edge.
  var SLACK = 20;
  function room(h, allowSlack) {
    var limit = PT.H - PT.M + (allowSlack ? SLACK : 0);
    if (y + h > limit) { doc.addPage(); y = PT.M; return true; }
    return false;
  }
  function para(text, size, color, style, lead, indent, slack) {
    if (!text) return;
    font(size, style, color);
    var lines = doc.splitTextToSize(String(text), W - (indent || 0));
    var lh = lead || size * 1.45;
    for (var i = 0; i < lines.length; i++) {
      room(lh, slack);
      doc.text(lines[i], x0 + (indent || 0), y + size * 0.8);
      y += lh;
    }
  }
  function sectionHead(label) {
    room(26);
    font(9, "bold", PT.CORAL);
    doc.text(label, x0, y + 8);
    y += 18;
  }

  // --- IDENTITY BANNER -------------------------------------------------------
  if (M.identity) {
    room(52);
    rgb("setFillColor", PT.NAVY);
    doc.roundedRect(x0, y, W, 46, 6, 6, "F");
    font(15, "bold", PT.WHITE);
    doc.text(M.identity.name, x0 + 14, y + 21);
    font(9, "normal", [190, 200, 215]);
    doc.text(M.identity.meta, x0 + 14, y + 36);
    y += 58;
  }

  // --- SESSION HEADER --------------------------------------------------------
  room(48);
  font(9, "bold", PT.BLUE);
  doc.text("SESSION REPORT", x0, y + 8);
  font(12, "normal", PT.NAVY);
  doc.text(M.sessionType, x0, y + 24);
  font(9, "normal", PT.G500);
  doc.text((M.date ? M.date + " · " : "") + "Evaluator: " + M.evaluator, x0, y + 38);
  y += 46;
  rgb("setDrawColor", PT.BLUE);
  doc.setLineWidth(1.5);
  doc.line(x0, y, x0 + W, y);
  y += 16;

  // --- PARTIAL ---------------------------------------------------------------
  if (M.isPartial) {
    room(30);
    rgb("setFillColor", PT.AMBER_BG);
    doc.roundedRect(x0, y, 52, 14, 3, 3, "F");
    font(8, "bold", PT.AMBER_FG);
    doc.text("PARTIAL", x0 + 8, y + 10);
    y += 20;
    if (M.partialText) { para(M.partialText, 9.5, PT.G700, "normal"); y += 4; }
  }

  // --- MEASURED STATS BAR ----------------------------------------------------
  // The device readings, printed as TEXT beside their labels. In the rasterised export these three
  // numbers were the least recoverable thing on the page and the most likely to be copied.
  if (M.stats.length) {
    room(46);
    rgb("setFillColor", PT.BLUE);
    doc.roundedRect(x0, y, W, 40, 6, 6, "F");
    var cw = W / M.stats.length;
    for (var i = 0; i < M.stats.length; i++) {
      var cx = x0 + cw * i + cw / 2;
      font(8, "normal", [205, 220, 240]);
      doc.text(M.stats[i][0], cx, y + 15, { align: "center" });
      // SHRINK TO FIT, NEVER TRUNCATE. A device reading can be long ("71-76 mph (catcher arm)")
      // and it was reaching the edge of its cell. Clipping a measured value to make it fit would
      // be the layout editing the record, so the type gives way instead.
      var val = String(M.stats[i][1]), fs = 15;
      font(fs, "bold", PT.WHITE);
      while (fs > 7 && doc.getTextWidth(val) > cw - 12) { fs -= 0.5; font(fs, "bold", PT.WHITE); }
      doc.text(val, cx, y + 32, { align: "center" });
    }
    y += 52;
  }

  // --- SUMMARY ---------------------------------------------------------------
  if (M.summary) {
    sectionHead("BBOT SUMMARY");
    var yTop = y;
    para(M.summary, 10.5, PT.G700, "normal", 15, 12);
    rgb("setFillColor", PT.CORAL);
    doc.rect(x0, yTop, 2.5, Math.max(0, y - yTop - 3), "F");
    if (M.identity) {
      room(16);
      font(9, "bold", PT.G500);
      doc.text("Bats", x0, y + 8);
      doc.text("Throws", x0 + 60, y + 8);
      font(9, "normal", PT.G500);
      doc.text(String(M.identity.bats), x0 + 26, y + 8);
      doc.text(String(M.identity.throws), x0 + 96, y + 8);
      y += 16;
    }
    y += 8;
  }

  // --- SESSION CONTEXT -------------------------------------------------------
  if (M.sessionContext) { para(M.sessionContext, 9.5, PT.G600, "italic"); y += 8; }

  // --- MEASURED TOOLS --------------------------------------------------------
  if (M.graded.length) {
    sectionHead("MEASURED TOOLS");
    for (var gi = 0; gi < M.graded.length; gi++) {
      var s = M.graded[gi];
      var conf = String(s.confidence || "").toLowerCase();
      var confirmed = conf.indexOf("confirmed") >= 0 || conf.indexOf("measured") >= 0;
      room(40);
      font(11, "bold", PT.NAVY);
      doc.text(cleanToolLabel(s.label), x0, y + 9);
      if (confirmed) {
        var lw = doc.getTextWidth(cleanToolLabel(s.label));
        rgb("setFillColor", PT.OK_BG);
        doc.roundedRect(x0 + lw + 8, y, 50, 12, 2, 2, "F");
        font(7.5, "bold", PT.OK_FG);
        doc.text("CONFIRMED", x0 + lw + 13, y + 8.5);
      }
      // THE NUMBER AND THE BAR BOTH DREW THE GRADE, so both go with it (zero-grades law).
      //
      // Nothing is printed in their place. The measured mark already has a home on this page in
      // the values block above, and a coach copying a number out of this document copies it from
      // there -- printing it twice is how one value becomes two that can disagree.
      // [[one-home-per-value]]
      //
      // AND NO AGE-PEER POSITION IS DRAWN HERE. That percentile comes off a fixed curve in
      // BbotBoxReport.js -- an anchor, a bracket multiplier, a fixed spread -- with no player, rep
      // or session in it, so it cannot state where anyone sits among peers. Removed, not switched
      // off: Luis, 2026-10-09, "the record doesn't keep two paths alive ... delete the flag." A
      // real placement is counted against a named pool with its n stated, and gets built then.
      y += 16;
      if (s.text) para(s.text, 9.5, PT.G700, "normal", 13);
      y += 5;
      if (gi < M.graded.length - 1) {
        rgb("setDrawColor", PT.G100); doc.setLineWidth(0.7);
        doc.line(x0, y - 3, x0 + W, y - 3);
      }
    }
    y += 6;
  }

  // --- OBSERVATIONS ----------------------------------------------------------
  if (M.observations.length) {
    sectionHead("SCOUTING OBSERVATIONS");
    for (var oi = 0; oi < M.observations.length; oi++) {
      var o = M.observations[oi];
      room(34);
      var oTop = y;
      font(9.5, "bold", PT.NAVY);
      doc.text(cleanToolLabel(o.label), x0 + 10, y + 8);
      y += 13;
      if (o.source) { para("Source: " + o.source, 7.5, PT.G500, "italic", 10, 10); }
      para(o.text, 9, PT.G700, "normal", 12.5, 10);
      rgb("setFillColor", PT.NAVY);
      doc.rect(x0, oTop, 2.5, Math.max(0, y - oTop - 2), "F");
      y += 6;
    }
    // no trailing gap: whatever follows brings its own separator (the NOT EVALUATED block opens
    // with a rule), and the spare points are the difference between one page and an orphan
  }

  // --- MEASURED MECHANICS ----------------------------------------------------
  // RULED 2026-10-12 BY LUIS: KEEP THIS, AND RE-POINT IT -- do not delete it.
  //
  // It was raised for deletion beside the percentile track and the kinetic-chain line, as a third
  // path that prints nothing today (0 of 39 reports carry `mechanicsCertified`). It is NOT the same
  // case, which is why it survives: those two were UNGATED and silent by accident, whereas this
  // sits behind six receipts including `scale_hand_measured` and `scale_domain_stable` -- the two
  // that certify the very scale problem that bars its numbers. It is a correct gate that has never
  // been satisfied, not an accident waiting to print.
  //
  // What it still must not do is print the INCUMBENT ratios. Stride, weight shift and head move
  // here come from `body_scale_px`: one body scale per rep applied across frames, which moves 11.6%
  // (MAD/median) within a session. The replacement is frame-local ratios -- every length divided by
  // a body length read in the SAME frame -- in pose_engine/baseball/frame_ratios.py.
  //
  // THE CHANGE WHEN PART 2b LANDS: read the frame-local values and their gate instead of
  // `_blk.reps[].{stride_length_direct_pct,weight_shift_pct,head_movement_pct}`. Until a reconciled
  // corpus sets that gate, this draws nothing and that is correct rather than lucky.
  if (M.mechanics) {
    sectionHead("MEASURED MECHANICS");
    para("Rep-to-rep — each dot is one measured " + M.mechanics.repType +
         " (" + M.mechanics.repCount + " tracked); a tighter cluster = more repeatable.",
         8, PT.G500, "italic", 11);
    y += 4;
    for (var mi = 0; mi < M.mechanics.rows.length; mi++) {
      var row = M.mechanics.rows[mi];
      room(18);
      font(8.5, "normal", PT.G600);
      doc.text(row.m.label, x0 + 78, y + 8, { align: "right" });
      var trackX = x0 + 88, trackW = 190;
      rgb("setFillColor", PT.G200);
      doc.rect(trackX, y + 6.2, trackW, 1.6, "F");
      var span = (row.mx - row.mn) || 1;
      for (var vi = 0; vi < row.vals.length; vi++) {
        var cxp = trackX + ((row.vals[vi] - row.mn) / span) * trackW;
        if (vi === 0) { rgb("setFillColor", PT.CORAL); doc.circle(cxp, y + 7, 3.4, "F"); }
        else { rgb("setDrawColor", PT.CORAL); doc.setLineWidth(0.8); doc.circle(cxp, y + 7, 2.4, "S"); }
      }
      // THE RANGE IS TEXT — the dots are the picture of it, and the picture is not the record.
      font(8.5, "normal", PT.G700);
      doc.text(Math.round(row.mn) + "–" + Math.round(row.mx) + row.m.unit, trackX + trackW + 12, y + 9);
      y += 16;
    }
    y += 8;
  }

  // --- NOT EVALUATED ---------------------------------------------------------
  // NAMED, NOT OMITTED. What BBOT could not see is part of what the document reports.
  if (M.notEvaluated.length) {
    // MEASURED, THEN PLACED WHOLE. Reserving a guessed height let the rule and the heading fit
    // while the list itself did not — which put a lone line on a page of its own, the orphan this
    // block is least able to afford. The height is computed from the wrapped lines, so the break
    // decision is made once, for the whole block, on the real number.
    var neText = M.notEvaluated.join(", ");
    font(8, "normal", PT.G500);
    var neLines = doc.splitTextToSize(neText, W);
    doc.__diag = { yBeforeNotEvaluated: y, blockH: 10 + 12 + neLines.length * 11, limit: PT.H - PT.M + SLACK };
    room(10 + 12 + neLines.length * 11, true);
    rgb("setDrawColor", PT.G200); doc.setLineWidth(0.7);
    doc.line(x0, y, x0 + W, y);
    y += 10;
    font(8.5, "bold", PT.G500);
    doc.text("NOT EVALUATED BY BBOT:", x0, y + 7);
    y += 12;
    font(8, "normal", PT.G500);
    for (var ni = 0; ni < neLines.length; ni++) { doc.text(neLines[ni], x0, y + 6.4); y += 11; }
  }

  return doc;
}
